import { useRef } from 'react';
import { EVENTS } from 'common/constants';
import useEventListener from 'common/hooks/useEventListener';

export interface IChatMethods {
  show: () => void;
  hide: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Hook that provides a ref object with chat control methods (open, close, and toggle) and listens for global chat events.
 *
 * @returns {RefObject<IChatMethods>} A ref object containing the chat control methods, which can be used to open, close, or toggle the chat.
 *
 * The hook also automatically listens for the following global events:
 * - `app-chat:open` – Opens the chat.
 * - `app-chat:close` – Closes the chat.
 * - `app-chat:toggle` – Toggles the chat's open/closed state.
 *
 * Usage notes:
 * - Pass the returned ref to a chat component that uses `forwardRef` and `useImperativeHandle` to implement `IChatMethods`.
 */
const useChatMethods = () => {
  const chatMethodsRef = useRef<IChatMethods>(null);

  const show = () => {
    chatMethodsRef.current?.show?.();
  };

  const hide = () => {
    chatMethodsRef.current?.hide?.();
  };

  const open = () => {
    chatMethodsRef.current?.open?.();
  };

  const close = () => {
    chatMethodsRef.current?.close?.();
  };

  const toggle = () => {
    chatMethodsRef.current?.toggle?.();
  };

  useEventListener(EVENTS.CHAT_SHOW, show);

  useEventListener(EVENTS.CHAT_HIDE, hide);

  useEventListener(EVENTS.CHAT_OPEN, open);

  useEventListener(EVENTS.CHAT_CLOSE, close);

  useEventListener(EVENTS.CHAT_TOGGLE, toggle);

  return chatMethodsRef;
};

export default useChatMethods;
